import React from "react";
import {
  CircularProgress,
  IconButton,
  TextField,
  alpha,
  makeStyles
} from "@material-ui/core";
import { Edit, ArrowDropDown, Delete } from "@material-ui/icons";
import Autocomplete from "@material-ui/lab/Autocomplete/Autocomplete";
import {
  useSearchProfiles_SearchLazyQuery,
  SearchProfile_GetValue,
  SearchProfileFragment,
  SearchProfileType
} from "@graphql";
import { Value, parseSearchProfileFields } from "./utils";
import { Body } from "@common";
import AddSearchProfileDialog from "@containers/VirtualSelector/SearchProfileSelector/AddSearchProfileDialog/AddSearchProfileDialog";
import { SearchProfileFormInput } from "@containers/VirtualSelector/SearchProfileSelector/AddSearchProfileDialog/SearchProfileFormInput";
import locale from "@bonliva-crm/locale";
import { ThemeType } from "@theme";

type Props = {
  type: SearchProfileType;
  onChange: (fields: any, value: any) => void;
  onRemove: (id: string) => void;
  value: Value;
};

export const SearchProfileSelector: React.FC<Props> = ({
  value = null,
  type,
  onChange,
  onRemove
}) => {
  const [open, setOpen] = React.useState(false);
  const [openDialog, setOpenDialog] = React.useState(false);
  const [text, setText] = React.useState("");
  const [profile, setProfile] = React.useState<SearchProfileFragment>(null);
  const classes = useStyles({ open });

  const [getOptions, { loading, data }] = useSearchProfiles_SearchLazyQuery();

  const handleSetOptions = () => {
    getOptions({
      variables: {
        filter: {
          text,
          type
        }
      }
    });
  };

  React.useEffect(() => {
    if (!loading) {
      handleSetOptions();
    }
  }, [open, text]);

  const getProfile = async (id: string) => {
    const { data } = await SearchProfile_GetValue.func({ id });
    const res = data?.searchProfileById;
    setProfile(res);
    return res;
  };

  const handleChange = async (value) => {
    if (value) {
      const { fields } = await getProfile(value._id);

      onChange(parseSearchProfileFields(fields), value);
    } else {
      onChange(null, null);
      setText("");
    }
  };

  const handleEdit = async (e: React.MouseEvent<HTMLButtonElement>, id: string) => {
    e.stopPropagation();
    await getProfile(id);
    setOpenDialog(true);
  };

  const handleRemove = async (e: React.MouseEvent<HTMLButtonElement>, id: string) => {
    e.stopPropagation();
    onRemove(id);
  };

  const getSearchProfileInput = () => {
    const input: SearchProfileFormInput =
      SearchProfileFormInput.CreateFromFragment(profile);

    return input;
  };

  return (
    <>
      <Autocomplete
        freeSolo
        open={open}
        onOpen={() => {
          setOpen(true);
        }}
        onClose={() => {
          setOpen(false);
          setText("");
        }}
        onChange={(e, newValue) => handleChange(newValue)}
        options={data?.SearchProfiles?.items ?? []}
        loading={loading}
        value={value}
        getOptionSelected={(option, value) => option._id === value._id}
        getOptionLabel={(option) => option.name}
        renderOption={(option: Value, { selected }) => (
          <div className={classes.option}>
            <Body>
              {option.name}
              <br />
              {option.description}
            </Body>
            <div className={classes.optionActions}>
              <IconButton aria-label="edit" onClick={(e) => handleEdit(e, option._id)}>
                <Edit />
              </IconButton>
              <IconButton
                aria-label="delete"
                onClick={(e) => handleRemove(e, option._id)}
              >
                <Delete />
              </IconButton>
            </div>
          </div>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={locale.SearchProfiles}
            fullWidth
            value={text}
            onChange={(e) => setText(e.target.value)}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                  <IconButton
                    disableRipple
                    className={classes.arrowBtn}
                    onClick={() => setOpen(!open)}
                  >
                    <ArrowDropDown className={classes.arrowIcon} />
                  </IconButton>
                </React.Fragment>
              )
            }}
          />
        )}
      />
      <AddSearchProfileDialog
        getInput={getSearchProfileInput}
        open={openDialog}
        hideCustomer={type === SearchProfileType.Bookings}
        onClose={() => setOpenDialog(false)}
        id={profile?._id}
      />
    </>
  );
};

const useStyles = makeStyles((theme: ThemeType) => ({
  option: { display: "flex", width: "100%", alignItems: "center" },
  optionActions: { marginLeft: "auto" },
  arrowIcon: { fontSize: 15, color: alpha(theme.palette.common.black, 0.54) },
  arrowBtn: {
    padding: 4,
    marginRight: 4,
    backgroundColor: "transparent !important",
    position: "absolute",
    right: "25px",
    transform: ({ open }: { open: boolean }) => `rotate(${open ? 180 : 0}deg)`
  }
}));
